// src/cockpit4/components/HermesMultimodalInput.tsx
// ============================================
// Hermes 多模态交互（Hermes 助手 P1-功能7）
//
// 设计理念：Hermes 不仅能听会说，还能看懂图片、读懂文件、捕获屏幕。
//   1. 语音输入（SpeechRecognition 实时识别中文，可发送给 Hermes）
//   2. 语音输出（SpeechSynthesis 文字转语音，可选语音/语速/音调）
//   3. 图片理解（本地 FileReader 读取，提取尺寸/类型/主色调/文件名）
//   4. 文件理解（txt/md/json/csv 本地读取，生成前 500 字摘要）
//   5. 屏幕截图（getDisplayMedia 截屏并展示）
//   6. 手写识别 / 表情情绪识别（规划中，显示「即将支持」）
//   7. 多模态历史记录管理 + 内容发送给 Hermes 对话
//
// 数据来源（禁止假数据）：
//   - 用户上传文件/图片：浏览器 FileReader API 本地读取（不上传服务器）
//   - 语音识别：Web Speech API（SpeechRecognition，兼容 webkit 前缀）
//   - 语音合成：Web Speech API（SpeechSynthesis）
//   - 屏幕截图：navigator.mediaDevices.getDisplayMedia（需用户授权，失败友好提示）
//   - 历史缓存：localStorage（key: xeaos_hermes_multimodal）
//   - 无数据时显示「上传文件或开始录音」
//
// 内容发送：通过 CustomEvent('xeaos:hermes:send') 派发至 Hermes 对话
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic,
  MicOff,
  Square,
  Play,
  Volume2,
  VolumeX,
  Image as ImageIcon,
  FileText,
  Monitor,
  PenLine,
  Smile,
  Upload,
  Trash2,
  Send,
  Sparkles,
  Loader2,
  X,
  Clock,
  Hash,
  Palette,
  Ruler,
  FileType,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 多模态类型 */
type ModalityType =
  | "voice"
  | "image"
  | "file"
  | "screenshot"
  | "handwriting"
  | "emotion";

/** 多模态记录（与功能规格数据模型一致，扩展 mainColor 用于主色调展示） */
interface MultimodalRecord {
  id: string;
  type: ModalityType;
  /** 语音转文字 */
  transcript?: string;
  /** 图片 base64 预览 */
  imagePreview?: string;
  /** 图片元信息 */
  imageInfo?: {
    name: string;
    size: number;
    type: string;
    width?: number;
    height?: number;
    mainColor?: string; // 主色调（hex），功能规格要求提取
  };
  /** 文件内容 */
  fileContent?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  /** 文件摘要（前 500 字） */
  fileSummary?: string;
  /** 截图尺寸信息 */
  screenshotInfo?: { width: number; height: number };
  /** 通用 */
  createdAt: string;
  processed: boolean;
}

// ============================================
// Web Speech API 类型声明（标准 lib 未包含 SpeechRecognition）
// ============================================

interface ISpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface ISpeechRecognitionResult {
  readonly length: number;
  [index: number]: ISpeechRecognitionAlternative;
  isFinal: boolean;
}
interface ISpeechRecognitionResultList {
  readonly length: number;
  [index: number]: ISpeechRecognitionResult;
}
interface ISpeechRecognitionEvent {
  resultIndex: number;
  results: ISpeechRecognitionResultList;
}
interface ISpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}
interface ISpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onend: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onerror:
    | ((this: ISpeechRecognition, ev: ISpeechRecognitionErrorEvent) => void)
    | null;
  onresult:
    | ((this: ISpeechRecognition, ev: ISpeechRecognitionEvent) => void)
    | null;
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_hermes_multimodal";

/** 紫色主色调 */
const PRIMARY = "#A855F7";

/** 发送给 Hermes 的 CustomEvent 名称 */
const HERMES_SEND_EVENT = "xeaos:hermes:send";

/** 允许上传的文件类型 */
const ACCEPTED_FILE_TYPES = [".txt", ".md", ".json", ".csv"];
const ACCEPTED_FILE_MIME = [
  "text/plain",
  "text/markdown",
  "application/json",
  "text/csv",
];

/** 文件摘要最大字符数 */
const SUMMARY_MAX = 500;

/** 模态类型 → 图标/标签/可用性映射 */
const TYPE_META: Record<
  ModalityType,
  {
    icon: typeof Mic;
    label: string;
    desc: string;
    available: boolean;
  }
> = {
  voice: { icon: Mic, label: "语音", desc: "SpeechRecognition 识别", available: true },
  image: { icon: ImageIcon, label: "图片", desc: "本地图片理解", available: true },
  file: { icon: FileText, label: "文件", desc: "txt/md/json/csv", available: true },
  screenshot: { icon: Monitor, label: "截图", desc: "getDisplayMedia", available: true },
  handwriting: { icon: PenLine, label: "手写", desc: "即将支持", available: false },
  emotion: { icon: Smile, label: "情绪", desc: "即将支持", available: false },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 读取 localStorage 历史记录 */
function loadRecords(): MultimodalRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as MultimodalRecord[];
  } catch {
    return [];
  }
}

/** 瘦身记录（剥离大字段，仅保留元信息，用于配额超限降级） */
function slimRecord(r: MultimodalRecord): MultimodalRecord {
  return { ...r, imagePreview: undefined, fileContent: undefined };
}

/** 写入 localStorage（配额超限时降级瘦身） */
function saveRecords(
  records: MultimodalRecord[]
): { ok: boolean; msg?: string } {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    return { ok: true };
  } catch {
    // 配额超限：剥离大字段后重试
    try {
      const slim = records.map(slimRecord);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
      return { ok: true, msg: "本地存储空间不足，已省略部分预览内容" };
    } catch {
      return { ok: false, msg: "本地存储失败，历史将不持久化" };
    }
  }
}

/** 格式化字节为可读字符串 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** 格式化为相对时间 */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} 天前`;
  return new Date(iso).toLocaleString("zh-CN");
}

/** 获取 SpeechRecognition 构造函数（兼容 webkit 前缀） */
function getSpeechRecognitionCtor(): { new (): ISpeechRecognition } | null {
  const w = window as unknown as {
    SpeechRecognition?: { new (): ISpeechRecognition };
    webkitSpeechRecognition?: { new (): ISpeechRecognition };
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** FileReader 读取文件为文本 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("文件读取失败"));
    reader.readAsText(file);
  });
}

/** FileReader 读取文件为 DataURL（base64） */
function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
}

/** 缩放图片为预览图（限制最大边长，降低 localStorage 占用） */
function downscaleImage(
  dataUrl: string,
  maxSize = 480,
  quality = 0.82
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.max(1, Math.round(width * ratio));
        height = Math.max(1, Math.round(height * ratio));
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** 从图片提取主色调（量化采样，返回 hex） */
function extractMainColor(img: HTMLImageElement): string {
  try {
    const canvas = document.createElement("canvas");
    const size = 32;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return PRIMARY;
    ctx.drawImage(img, 0, 0, size, size);
    const data = ctx.getImageData(0, 0, size, size).data;
    // 量化到 32 为步长的颜色桶
    const buckets: Record<string, number> = {};
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 128) continue; // 跳过透明像素
      const r = Math.round(data[i] / 32) * 32;
      const g = Math.round(data[i + 1] / 32) * 32;
      const b = Math.round(data[i + 2] / 32) * 32;
      const key = `${r},${g},${b}`;
      buckets[key] = (buckets[key] ?? 0) + 1;
    }
    let bestKey = "168,85,247";
    let bestCount = 0;
    for (const k of Object.keys(buckets)) {
      if (buckets[k] > bestCount) {
        bestCount = buckets[k];
        bestKey = k;
      }
    }
    const [r, g, b] = bestKey.split(",").map(Number);
    return `#${[r, g, b]
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")}`;
  } catch {
    return PRIMARY;
  }
}

/** 加载图片元素（用于获取尺寸与主色调） */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("图片加载失败"));
    img.src = dataUrl;
  });
}

/** 生成文件摘要（前 500 字，去除多余空白） */
function summarizeFile(content: string): string {
  const cleaned = content.replace(/\s+/g, " ").trim();
  if (cleaned.length <= SUMMARY_MAX) return cleaned;
  return cleaned.slice(0, SUMMARY_MAX) + "…";
}

/** 校验文件是否为允许的文档类型 */
function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (ACCEPTED_FILE_TYPES.some((ext) => name.endsWith(ext))) return true;
  if (ACCEPTED_FILE_MIME.includes(file.type)) return true;
  return false;
}

/** 调用 getDisplayMedia 捕获屏幕截图 */
async function captureScreenshot(): Promise<{
  dataUrl: string;
  width: number;
  height: number;
}> {
  const mediaDevices = navigator.mediaDevices;
  if (!mediaDevices || !mediaDevices.getDisplayMedia) {
    throw new Error("当前浏览器不支持屏幕截图（getDisplayMedia）");
  }
  const stream = await mediaDevices.getDisplayMedia({
    video: true,
    audio: false,
  });
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;
  await video.play().catch(() => {
    // 某些浏览器 play 可能被拦截，忽略即可
  });
  // 等待视频帧就绪
  await new Promise<void>((resolve) => {
    if (video.readyState >= 2) resolve();
    else {
      video.onloadeddata = () => resolve();
      // 兜底超时
      setTimeout(() => resolve(), 1500);
    }
  });
  const width = video.videoWidth || 1280;
  const height = video.videoHeight || 720;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error("无法获取 Canvas 2D 上下文");
  }
  ctx.drawImage(video, 0, 0, width, height);
  const dataUrl = canvas.toDataURL("image/png");
  // 立即停止屏幕共享流
  stream.getTracks().forEach((t) => t.stop());
  return { dataUrl, width, height };
}

/** 构建发送给 Hermes 的文本内容 */
function buildSendContent(record: MultimodalRecord): string {
  switch (record.type) {
    case "voice":
      return record.transcript?.trim()
        ? record.transcript.trim()
        : "（语音内容为空）";
    case "image": {
      const info = record.imageInfo;
      const parts = ["[图片]"];
      if (info) {
        parts.push(info.name);
        if (info.width && info.height)
          parts.push(`${info.width}×${info.height}`);
        if (info.type) parts.push(info.type);
        parts.push(formatBytes(info.size));
        if (info.mainColor) parts.push(`主色调:${info.mainColor}`);
      }
      return parts.join(" | ");
    }
    case "file": {
      const parts = ["[文件]"];
      if (record.fileName) parts.push(record.fileName);
      if (record.fileType) parts.push(record.fileType);
      if (record.fileSize !== undefined) parts.push(formatBytes(record.fileSize));
      const summary = record.fileSummary?.trim();
      if (summary) parts.push(`\n摘要：\n${summary}`);
      return parts.join(" | ");
    }
    case "screenshot": {
      const info = record.screenshotInfo;
      const parts = ["[屏幕截图]"];
      if (info) parts.push(`${info.width}×${info.height}`);
      parts.push(formatRelativeTime(record.createdAt));
      return parts.join(" | ");
    }
    default:
      return `[${TYPE_META[record.type].label}]`;
  }
}

// ============================================
// 主组件
// ============================================

export default function HermesMultimodalInput() {
  // 历史记录
  const [records, setRecords] = useState<MultimodalRecord[]>(() => loadRecords());
  // 当前激活的模态标签
  const [activeTab, setActiveTab] = useState<ModalityType>("voice");
  const [toast, setToast] = useState<string | null>(null);

  // ---------- 语音识别状态 ----------
  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState(""); // 实时中间结果
  const [finalTranscript, setFinalTranscript] = useState(""); // 累计最终结果
  const [speechSupported, setSpeechSupported] = useState(true);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // ---------- 语音合成状态 ----------
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string>("");
  const [ttsText, setTtsText] = useState("");
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  // v6.2.16 语音播报开关：默认关闭，持久化到 localStorage（用户诉求"生成中时间久，能否关闭语音播报"）
  const [ttsAutoPlay, setTtsAutoPlay] = useState<boolean>(() => {
    try {
      return localStorage.getItem("xeaos_tts_autoplay") === "1";
    } catch { return false; }
  });
  const toggleTtsAutoPlay = () => {
    const next = !ttsAutoPlay;
    setTtsAutoPlay(next);
    try { localStorage.setItem("xeaos_tts_autoplay", next ? "1" : "0"); } catch { /* ignore */ }
  };

  // ---------- 图片 / 文件状态 ----------
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<
    MultimodalRecord["imageInfo"] | null
  >(null);
  const [fileRecord, setFileRecord] = useState<{
    name: string;
    type: string;
    size: number;
    content: string;
    summary: string;
  } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---------- 历史展开 ----------
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // ============================================
  // 持久化：records 变更写回 localStorage
  // ============================================
  useEffect(() => {
    const res = saveRecords(records);
    if (!res.ok) {
      setToast(res.msg ?? "本地存储失败");
    } else if (res.msg) {
      setToast(res.msg);
    }
  }, [records]);

  // Toast 自动消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================
  // 初始化：检测 SpeechRecognition 支持并加载 TTS 语音列表
  // ============================================
  useEffect(() => {
    const ctor = getSpeechRecognitionCtor();
    if (!ctor) setSpeechSupported(false);

    // 加载语音合成可用语音
    const loadVoices = () => {
      try {
        const list = window.speechSynthesis?.getVoices() ?? [];
        setVoices(list);
        if (list.length > 0 && !selectedVoiceURI) {
          // 优先选中文语音
          const zh = list.find((v) => /zh|cmn|chinese/i.test(v.lang));
          setSelectedVoiceURI((zh ?? list[0]).voiceURI);
        }
      } catch {
        // 静默降级
      }
    };
    loadVoices();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
    // 仅初始化一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 新增记录并清空对应输入区
  // ============================================
  const addRecord = useCallback((rec: MultimodalRecord) => {
    setRecords((prev) => [rec, ...prev].slice(0, 100));
  }, []);

  // ============================================
  // 语音识别：开始录音
  // ============================================
  const startRecording = useCallback(() => {
    if (!speechSupported) {
      setVoiceError("当前浏览器不支持语音识别（SpeechRecognition）");
      return;
    }
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setVoiceError("当前浏览器不支持语音识别（SpeechRecognition）");
      return;
    }
    setVoiceError(null);
    setInterim("");
    setFinalTranscript("");
    const recognition = new Ctor();
    recognition.lang = "zh-CN";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setRecording(true);
    };
    recognition.onresult = (ev: ISpeechRecognitionEvent) => {
      let interimText = "";
      let finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) finalText += transcript;
        else interimText += transcript;
      }
      if (finalText) {
        setFinalTranscript((prev) => prev + finalText);
      }
      setInterim(interimText);
    };
    recognition.onerror = (ev: ISpeechRecognitionErrorEvent) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setVoiceError("麦克风权限被拒绝，请允许后重试");
      } else if (ev.error === "no-speech") {
        setVoiceError("未检测到语音输入");
      } else if (ev.error === "network") {
        setVoiceError("语音识别网络异常");
      } else {
        setVoiceError(`语音识别错误：${ev.error}`);
      }
    };
    recognition.onend = () => {
      setRecording(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      setVoiceError(`启动录音失败：${(err as Error).message}`);
      setRecording(false);
    }
  }, [speechSupported]);

  // ============================================
  // 语音合成：播放
  // ============================================
  const speak = useCallback(
    (text: string) => {
      if (!window.speechSynthesis) {
        setToast("当前浏览器不支持语音合成（SpeechSynthesis）");
        return;
      }
      const content = text.trim();
      if (!content) {
        setToast("请输入需要合成的文字");
        return;
      }
      // 停止上一段
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(content);
      utter.lang = "zh-CN";
      utter.rate = rate;
      utter.pitch = pitch;
      const voice =
        voices.find((v) => v.voiceURI === selectedVoiceURI) ?? null;
      if (voice) utter.voice = voice;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utter);
    },
    [voices, selectedVoiceURI, rate, pitch]
  );

  // ============================================
  // 语音识别：停止录音并保存记录
  // ============================================
  const stopRecording = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition) {
      try {
        recognition.stop();
      } catch {
        // 静默降级
      }
    }
    setRecording(false);
    // 使用 setTimeout 等待最后一次 onresult 落盘
    setTimeout(() => {
      setFinalTranscript((prev) => {
        const text = (prev + interim).trim();
        setInterim("");
        if (text) {
          addRecord({
            id: genId(),
            type: "voice",
            transcript: text,
            createdAt: new Date().toISOString(),
            processed: false,
          });
          setTtsText(text);
          setToast("语音识别完成，已加入历史");
          // v6.2.16 自动播报：开关开启时自动朗读识别结果
          if (ttsAutoPlay) {
            speak(text);
          }
        } else {
          setToast("未识别到有效语音内容");
        }
        return prev;
      });
    }, 200);
  }, [interim, addRecord, ttsAutoPlay, speak]);

  // ============================================
  // 语音合成：停止
  // ============================================
  const stopSpeak = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  }, []);

  // 组件卸载时停止合成与识别
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      try {
        recognitionRef.current?.abort();
      } catch {
        // 静默
      }
    };
  }, []);

  // ============================================
  // 图片处理：从 File 解析预览 + 元信息 + 主色调
  // ============================================
  const handleImageFile = useCallback(
    async (file: File) => {
      setProcessing(true);
      setToast(null);
      try {
        if (!file.type.startsWith("image/")) {
          setToast("请上传图片文件");
          return;
        }
        const rawUrl = await readFileAsDataURL(file);
        const previewUrl = await downscaleImage(rawUrl);
        const img = await loadImage(rawUrl);
        const mainColor = extractMainColor(img);
        const info: MultimodalRecord["imageInfo"] = {
          name: file.name,
          size: file.size,
          type: file.type,
          width: img.naturalWidth,
          height: img.naturalHeight,
          mainColor,
        };
        setImagePreview(previewUrl);
        setImageInfo(info);
      } catch (err) {
        setToast(`图片处理失败：${(err as Error).message}`);
      } finally {
        setProcessing(false);
      }
    },
    []
  );

  // ============================================
  // 文件处理：从 File 读取内容并生成摘要
  // ============================================
  const handleDocFile = useCallback(async (file: File) => {
    setProcessing(true);
    setToast(null);
    try {
      if (!isAcceptedFile(file)) {
        setToast("仅支持 txt / md / json / csv 文件");
        return;
      }
      const content = await readFileAsText(file);
      const summary = summarizeFile(content);
      setFileRecord({
        name: file.name,
        type: file.type || "text/plain",
        size: file.size,
        content,
        summary,
      });
    } catch (err) {
      setToast(`文件读取失败：${(err as Error).message}`);
    } finally {
      setProcessing(false);
    }
  }, []);

  // ============================================
  // 保存图片记录到历史
  // ============================================
  const saveImageRecord = useCallback(() => {
    if (!imagePreview || !imageInfo) {
      setToast("请先上传图片");
      return;
    }
    addRecord({
      id: genId(),
      type: "image",
      imagePreview,
      imageInfo,
      createdAt: new Date().toISOString(),
      processed: false,
    });
    setImagePreview(null);
    setImageInfo(null);
    setToast("图片已加入历史");
  }, [imagePreview, imageInfo, addRecord]);

  // ============================================
  // 保存文件记录到历史
  // ============================================
  const saveFileRecord = useCallback(() => {
    if (!fileRecord) {
      setToast("请先上传文件");
      return;
    }
    addRecord({
      id: genId(),
      type: "file",
      fileName: fileRecord.name,
      fileType: fileRecord.type,
      fileSize: fileRecord.size,
      fileContent: fileRecord.content,
      fileSummary: fileRecord.summary,
      createdAt: new Date().toISOString(),
      processed: false,
    });
    setFileRecord(null);
    setToast("文件已加入历史");
  }, [fileRecord, addRecord]);

  // ============================================
  // 屏幕截图
  // ============================================
  const handleScreenshot = useCallback(async () => {
    setProcessing(true);
    setToast(null);
    try {
      const { dataUrl, width, height } = await captureScreenshot();
      const previewUrl = await downscaleImage(dataUrl, 640, 0.85);
      addRecord({
        id: genId(),
        type: "screenshot",
        imagePreview: previewUrl,
        screenshotInfo: { width, height },
        createdAt: new Date().toISOString(),
        processed: false,
      });
      setToast("屏幕截图已加入历史");
    } catch (err) {
      const msg = (err as Error).message || "截图失败";
      // 用户取消授权时给出友好提示
      if (/not allowed|denied|permission/i.test(msg)) {
        setToast("屏幕截图授权被拒绝，请允许后重试");
      } else {
        setToast(`截图失败：${msg}`);
      }
    } finally {
      setProcessing(false);
    }
  }, [addRecord]);

  // ============================================
  // 删除 / 清空历史
  // ============================================
  const removeRecord = useCallback((id: string) => {
    setRecords((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const clearAllRecords = useCallback(() => {
    setRecords([]);
    setToast("已清空全部历史");
  }, []);

  // ============================================
  // 发送记录内容给 Hermes 对话
  // ============================================
  const sendToHermes = useCallback(
    (record: MultimodalRecord) => {
      const content = buildSendContent(record);
      window.dispatchEvent(
        new CustomEvent(HERMES_SEND_EVENT, {
          detail: {
            source: "multimodal",
            type: record.type,
            content,
            recordId: record.id,
            createdAt: record.createdAt,
          },
        })
      );
      // 标记为已处理
      setRecords((prev) =>
        prev.map((r) =>
          r.id === record.id ? { ...r, processed: true } : r
        )
      );
      setToast(`已发送给 Hermes（${TYPE_META[record.type].label}）`);
    },
    []
  );

  // ============================================
  // 拖拽上传处理
  // ============================================
  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (activeTab === "image") {
        handleImageFile(file);
      } else if (activeTab === "file") {
        handleDocFile(file);
      }
    },
    [activeTab, handleImageFile, handleDocFile]
  );

  // ============================================
  // 可用标签（仅展示可用 + 规划中）
  // ============================================
  const tabs = useMemo<ModalityType[]>(
    () => ["voice", "image", "file", "screenshot", "handwriting", "emotion"],
    []
  );

  // 历史统计
  const stats = useMemo(() => {
    const total = records.length;
    const processed = records.filter((r) => r.processed).length;
    return { total, processed };
  }, [records]);

  // ============================================
  // 渲染：标签按钮
  // ============================================
  const renderTab = (tab: ModalityType) => {
    const meta = TYPE_META[tab];
    const Icon = meta.icon;
    const active = activeTab === tab;
    return (
      <button
        key={tab}
        onClick={() => meta.available && setActiveTab(tab)}
        disabled={!meta.available}
        className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all border ${
          active
            ? "border-purple-500/40 bg-purple-500/15 text-purple-200"
            : meta.available
            ? "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:bg-white/[0.05] hover:text-purple-200"
            : "border-white/[0.04] bg-white/[0.01] text-gray-600 cursor-not-allowed"
        }`}
        title={meta.desc}
      >
        <Icon size={12} />
        <span>{meta.label}</span>
        {!meta.available && (
          <span className="px-1 py-0 rounded text-[7px] font-mono bg-amber-500/10 text-amber-400/80">
            即将支持
          </span>
        )}
        {active && (
          <motion.span
            layoutId="multimodal-tab-active"
            className="absolute -bottom-px left-2 right-2 h-px bg-purple-400/60"
          />
        )}
      </button>
    );
  };

  // ============================================
  // 渲染：语音操作区
  // ============================================
  const renderVoicePanel = () => (
    <div className="space-y-3">
      {/* 录音按钮 + 实时识别 */}
      <div className="flex items-center gap-3">
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={!speechSupported}
          className={`relative flex items-center justify-center w-14 h-14 rounded-full border transition-all ${
            recording
              ? "border-red-500/50 bg-red-500/15"
              : "border-purple-500/40 bg-purple-500/15 hover:bg-purple-500/25"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
          title={recording ? "停止录音" : "开始录音（中文识别）"}
        >
          {recording ? (
            <Square size={18} className="text-red-400" />
          ) : speechSupported ? (
            <Mic size={20} className="text-purple-300" />
          ) : (
            <MicOff size={20} className="text-gray-500" />
          )}
          {recording && (
            <motion.span
              className="absolute inset-0 rounded-full border border-red-500/40"
              animate={{ scale: [1, 1.25], opacity: [0.6, 0] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              {recording ? "正在识别…" : "语音输入"}
            </span>
            {recording && (
              <Loader2 size={10} className="text-red-400 animate-spin" />
            )}
          </div>
          <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2 min-h-[44px] text-[11px] leading-relaxed">
            {finalTranscript || interim ? (
              <span>
                <span className="text-white">{finalTranscript}</span>
                <span className="text-gray-500">{interim}</span>
              </span>
            ) : (
              <span className="text-gray-600">
                {speechSupported
                  ? "点击麦克风开始录音，将实时识别中文语音"
                  : "当前浏览器不支持语音识别"}
              </span>
            )}
          </div>
        </div>
      </div>

      {voiceError && (
        <div className="px-3 py-1.5 rounded-md border border-red-500/30 bg-red-500/[0.06] text-[10px] font-mono text-red-300">
          {voiceError}
        </div>
      )}

      {/* 语音合成 */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Volume2 size={12} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            语音合成（文字转语音）
          </span>
        </div>
        <textarea
          value={ttsText}
          onChange={(e) => setTtsText(e.target.value)}
          placeholder="输入或粘贴文字，点击播放即可合成语音"
          rows={2}
          className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-[11px] text-white outline-none focus:border-purple-500/40 resize-none placeholder:text-gray-600"
        />
        {/* 语音 / 语速 / 音调 */}
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-3 sm:col-span-1">
            <label className="text-[8px] font-mono text-gray-500 uppercase">
              语音
            </label>
            <select
              value={selectedVoiceURI}
              onChange={(e) => setSelectedVoiceURI(e.target.value)}
              className="w-full mt-0.5 px-1.5 py-1 rounded bg-white/[0.04] border border-white/[0.06] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
            >
              {voices.length === 0 && (
                <option value="">默认语音</option>
              )}
              {voices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[8px] font-mono text-gray-500 uppercase">
              语速 {rate.toFixed(1)}
            </label>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              className="w-full mt-1 accent-purple-500"
            />
          </div>
          <div>
            <label className="text-[8px] font-mono text-gray-500 uppercase">
              音调 {pitch.toFixed(1)}
            </label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={pitch}
              onChange={(e) => setPitch(Number(e.target.value))}
              className="w-full mt-1 accent-purple-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* v6.2.16 语音播报开关：默认关闭；开启后语音识别转文字自动朗读（用户诉求） */}
          <button
            onClick={toggleTtsAutoPlay}
            className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
              ttsAutoPlay
                ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
                : "bg-white/[0.03] border border-white/[0.06] text-gray-400"
            }`}
            title={ttsAutoPlay ? "自动播报已开启：语音识别转文字后自动朗读（点击关闭）" : "自动播报已关闭：需手动点「播放」朗读（点击开启）"}
          >
            {ttsAutoPlay ? <Volume2 size={11} /> : <VolumeX size={11} />}
            <span className="text-[9px] font-mono uppercase tracking-wider">
              {ttsAutoPlay ? "自动播报·开" : "自动播报·关"}
            </span>
          </button>
          <button
            onClick={() => speak(ttsText)}
            disabled={speaking}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
          >
            <Play size={11} className="text-purple-300" />
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              播放
            </span>
          </button>
          <button
            onClick={stopSpeak}
            disabled={!speaking}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors disabled:opacity-40"
          >
            <VolumeX size={11} className="text-gray-400" />
            <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
              停止
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // 渲染：图片操作区
  // ============================================
  const renderImagePanel = () => (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => imageInputRef.current?.click()}
        className={`cursor-pointer rounded-md border-2 border-dashed p-4 text-center transition-colors ${
          dragOver
            ? "border-purple-500/50 bg-purple-500/[0.06]"
            : "border-white/[0.08] bg-white/[0.02] hover:border-purple-500/30 hover:bg-white/[0.03]"
        }`}
      >
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleImageFile(f);
            e.target.value = "";
          }}
        />
        <Upload size={20} className="mx-auto text-purple-400 mb-1" />
        <p className="text-[11px] font-mono text-gray-300">
          点击或拖拽上传图片
        </p>
        <p className="text-[9px] font-mono text-gray-600 mt-0.5">
          本地 FileReader 读取，不上传服务器
        </p>
      </div>

      {processing && (
        <div className="flex items-center justify-center py-3">
          <Loader2 size={14} className="text-purple-400 animate-spin" />
        </div>
      )}

      {imagePreview && imageInfo && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md border border-white/[0.06] bg-white/[0.02] overflow-hidden"
        >
          <div className="flex gap-3 p-3">
            <img
              src={imagePreview}
              alt={imageInfo.name}
              className="w-20 h-20 object-cover rounded border border-white/[0.06] flex-shrink-0"
            />
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-1.5">
                <FileText size={10} className="text-purple-400 flex-shrink-0" />
                <span className="text-[11px] text-white truncate">
                  {imageInfo.name}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Ruler size={10} className="text-gray-500 flex-shrink-0" />
                <span className="text-[10px] font-mono text-gray-300">
                  {imageInfo.width}×{imageInfo.height}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <FileType size={10} className="text-gray-500 flex-shrink-0" />
                <span className="text-[10px] font-mono text-gray-300">
                  {imageInfo.type}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Hash size={10} className="text-gray-500 flex-shrink-0" />
                <span className="text-[10px] font-mono text-gray-300">
                  {formatBytes(imageInfo.size)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Palette size={10} className="text-gray-500 flex-shrink-0" />
                <span
                  className="inline-block w-3 h-3 rounded border border-white/[0.1]"
                  style={{ background: imageInfo.mainColor ?? PRIMARY }}
                />
                <span className="text-[10px] font-mono text-gray-300">
                  {imageInfo.mainColor ?? PRIMARY}
                </span>
              </div>
            </div>
          </div>
          <div className="px-3 pb-3">
            <button
              onClick={saveImageRecord}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
            >
              <Sparkles size={11} className="text-purple-300" />
              <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
                加入历史
              </span>
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );

  // ============================================
  // 渲染：文件操作区
  // ============================================
  const renderFilePanel = () => (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`cursor-pointer rounded-md border-2 border-dashed p-4 text-center transition-colors ${
          dragOver
            ? "border-purple-500/50 bg-purple-500/[0.06]"
            : "border-white/[0.08] bg-white/[0.02] hover:border-purple-500/30 hover:bg-white/[0.03]"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleDocFile(f);
            e.target.value = "";
          }}
        />
        <Upload size={20} className="mx-auto text-purple-400 mb-1" />
        <p className="text-[11px] font-mono text-gray-300">
          点击或拖拽上传文档
        </p>
        <p className="text-[9px] font-mono text-gray-600 mt-0.5">
          支持 txt / md / json / csv，本地读取
        </p>
      </div>

      {processing && (
        <div className="flex items-center justify-center py-3">
          <Loader2 size={14} className="text-purple-400 animate-spin" />
        </div>
      )}

      {fileRecord && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 space-y-2"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <FileText size={12} className="text-purple-400" />
            <span className="text-[11px] text-white truncate max-w-[160px]">
              {fileRecord.name}
            </span>
            <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-purple-500/10 text-purple-300">
              {fileRecord.type}
            </span>
            <span className="text-[9px] font-mono text-gray-500">
              {formatBytes(fileRecord.size)}
            </span>
          </div>
          <div>
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              摘要（前 {SUMMARY_MAX} 字）
            </div>
            <div className="rounded bg-white/[0.02] border border-white/[0.04] px-2 py-1.5 text-[10px] text-gray-300 leading-relaxed max-h-[100px] overflow-y-auto">
              {fileRecord.summary || "（空文件）"}
            </div>
          </div>
          <button
            onClick={saveFileRecord}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
          >
            <Sparkles size={11} className="text-purple-300" />
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              加入历史
            </span>
          </button>
        </motion.div>
      )}
    </div>
  );

  // ============================================
  // 渲染：屏幕截图操作区
  // ============================================
  const renderScreenshotPanel = () => (
    <div className="space-y-3">
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-4 text-center">
        <Monitor size={24} className="mx-auto text-purple-400 mb-2" />
        <p className="text-[11px] font-mono text-gray-300 mb-1">
          屏幕截图理解
        </p>
        <p className="text-[9px] font-mono text-gray-600 mb-3">
          调用 getDisplayMedia，需授权屏幕共享
        </p>
        <button
          onClick={handleScreenshot}
          disabled={processing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
        >
          {processing ? (
            <Loader2 size={12} className="text-purple-300 animate-spin" />
          ) : (
            <Monitor size={12} className="text-purple-300" />
          )}
          <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
            {processing ? "截图中…" : "开始截图"}
          </span>
        </button>
      </div>
    </div>
  );

  // ============================================
  // 渲染：规划中模态
  // ============================================
  const renderComingSoon = (tab: ModalityType) => {
    const meta = TYPE_META[tab];
    const Icon = meta.icon;
    return (
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-6 text-center">
        <Icon size={24} className="mx-auto text-gray-600 mb-2" />
        <p className="text-[11px] font-mono text-gray-400">
          {meta.label}识别 · 即将支持
        </p>
        <p className="text-[9px] font-mono text-gray-600 mt-1">
          该模态正在规划中，敬请期待
        </p>
      </div>
    );
  };

  // ============================================
  // 渲染：当前操作区
  // ============================================
  const renderActivePanel = () => {
    switch (activeTab) {
      case "voice":
        return renderVoicePanel();
      case "image":
        return renderImagePanel();
      case "file":
        return renderFilePanel();
      case "screenshot":
        return renderScreenshotPanel();
      case "handwriting":
      case "emotion":
        return renderComingSoon(activeTab);
      default:
        return null;
    }
  };

  // ============================================
  // 渲染：历史记录条目
  // ============================================
  const renderRecord = (rec: MultimodalRecord) => {
    const meta = TYPE_META[rec.type];
    const Icon = meta.icon;
    return (
      <motion.div
        key={rec.id}
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        className={`rounded-md border p-2.5 transition-colors ${
          rec.processed
            ? "border-white/[0.04] bg-white/[0.01] opacity-60"
            : "border-purple-500/20 bg-purple-500/[0.03]"
        }`}
      >
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-6 h-6 rounded bg-purple-500/15 flex items-center justify-center mt-0.5">
            <Icon size={11} className="text-purple-400" />
          </div>
          <div className="flex-1 min-w-0">
            {/* 内容预览 */}
            {rec.type === "voice" && (
              <p className="text-[10px] text-gray-200 leading-snug line-clamp-2">
                {rec.transcript || "（无内容）"}
              </p>
            )}
            {rec.type === "image" && rec.imagePreview && (
              <img
                src={rec.imagePreview}
                alt={rec.imageInfo?.name ?? "图片"}
                className="w-full max-h-32 object-cover rounded border border-white/[0.06] mb-1"
              />
            )}
            {rec.type === "screenshot" && rec.imagePreview && (
              <img
                src={rec.imagePreview}
                alt="屏幕截图"
                className="w-full max-h-32 object-cover rounded border border-white/[0.06] mb-1"
              />
            )}
            {rec.type === "file" && (
              <p className="text-[10px] text-gray-300 leading-snug line-clamp-2">
                <span className="text-purple-300">{rec.fileName}</span>
                {rec.fileSummary ? ` · ${rec.fileSummary}` : ""}
              </p>
            )}

            {/* 元信息行 */}
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-purple-500/10 text-purple-300">
                {meta.label}
              </span>
              {rec.processed && (
                <span className="px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-emerald-500/10 text-emerald-400">
                  已发送
                </span>
              )}
              <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-600">
                <Clock size={8} />
                {formatRelativeTime(rec.createdAt)}
              </span>
            </div>

            {/* 操作行 */}
            <div className="flex items-center gap-1 mt-1.5">
              <button
                onClick={() => sendToHermes(rec)}
                disabled={rec.processed}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-purple-300 hover:bg-purple-500/15 transition-colors disabled:opacity-40"
                title="发送该内容给 Hermes 对话"
              >
                <Send size={9} />
                {rec.processed ? "已发送" : "发送给 Hermes"}
              </button>
              <button
                onClick={() => removeRecord(rec.id)}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                title="删除该记录"
              >
                <Trash2 size={9} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // ============================================
  // 渲染主界面
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带（紫色主色调） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Sparkles size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              Hermes 多模态交互
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Multimodal Input · 语音 / 图片 / 文件 / 截图
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-white/[0.03] border border-white/[0.06] text-gray-400">
            历史 {stats.total}
          </span>
          <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-purple-500/10 border border-purple-500/20 text-purple-300">
            已发送 {stats.processed}
          </span>
        </div>
      </div>

      {/* 顶部：模态选择标签 */}
      <div className="px-4 py-2.5 border-b border-white/[0.04] flex items-center gap-1.5 flex-wrap">
        {tabs.map(renderTab)}
      </div>

      {/* 中部：当前模态操作区 */}
      <div className="p-4 border-b border-white/[0.04]">
        {renderActivePanel()}
      </div>

      {/* Toast 提示 */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-2">
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-purple-500/30 bg-purple-500/[0.06] text-[10px] font-mono text-purple-300">
                <span>{toast}</span>
                <button
                  onClick={() => setToast(null)}
                  className="text-purple-400 hover:text-white"
                >
                  <X size={11} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 底部：历史记录列表 */}
      <div className="border-t border-white/[0.04]">
        <button
          onClick={() => setHistoryExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
        >
          <div className="flex items-center gap-2">
            {historyExpanded ? (
              <ChevronDown size={12} className="text-purple-400" />
            ) : (
              <ChevronRight size={12} className="text-purple-400" />
            )}
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
              多模态历史
            </span>
            <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-purple-500/10 text-purple-300">
              {records.length}
            </span>
          </div>
          {records.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllRecords();
              }}
              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={9} />
              清空
            </button>
          )}
        </button>

        <AnimatePresence>
          {historyExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              {records.length === 0 ? (
                <div className="px-4 pb-6 pt-2 text-center">
                  <Upload size={24} className="mx-auto text-gray-700 mb-2" />
                  <p className="text-[11px] font-mono text-gray-500">
                    上传文件或开始录音
                  </p>
                  <p className="text-[9px] font-mono text-gray-600 mt-1">
                    识别 / 提取的内容将记录在此，可发送给 Hermes
                  </p>
                </div>
              ) : (
                <div className="px-4 pb-4 space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                  {records.map(renderRecord)}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
